import { useState } from "react";
import { Bell, Check, X, UserPlus, Users, Briefcase, Calendar, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { HeadingSmall, BodySmall, Caption } from "@/components/ui/typography";
import { useUser } from "@/hooks/auth";
import { supabase } from "@/integrations/supabase/client";
import { useUnifiedNotifications } from "@/hooks/useUnifiedNotifications";
import { useNotificationContext } from "@/contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { useJobDialogFromNotification } from "@/hooks/useJobDialogFromNotification";
import { JobDetailDialog } from "@/components/hiring/JobDetailDialog";
import { useTranslation } from "@/hooks/useTranslation";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useDataRefresh } from "@/hooks/useDataRefresh";


interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read: boolean;
  created_at: string;
  avatar_url?: string;
  is_invitation?: boolean;
  invitation_data?: {
    company_name: string;
    company_logo: string | null;
    member_role: string;
    invited_by: string;
  };
}

export function NotificationContent() {
  const { user } = useUser();
  const toast = useTranslatedToast();
  const { notifyCurrentUser, notifyErrorLocal } = useUnifiedNotifications();
  const { notifications, markAsRead, loadNotifications } = useNotificationContext();
  const navigate = useNavigate();
  const { 
    job, 
    loading: jobLoading, 
    dialogOpen, 
    openJobFromNotification, 
    closeDialog 
  } = useJobDialogFromNotification();
  const { t: tPages } = useTranslation('pages');
  const { t: tUI } = useTranslation('ui');
  const { fullRefresh } = useDataRefresh();
  
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);


  const handleInvitationResponse = async (notificationId: string, accept: boolean) => {
    if (!user) return;
    
    setProcessingInvitation(notificationId);
    
    try {
      // Get notification data
      const { data: notification } = await supabase
        .from('notifications')
        .select('data')
        .eq('id', notificationId as any)
        .single();
      
      const invitationData = (notification as any)?.data as any;
      if (!invitationData?.invitation_id) {
        notifyErrorLocal(
          tPages('notifications.invitation_invalid_title'),
          tPages('notifications.invitation_invalid')
        );
        return;
      }
      
      if (accept) {
        // Call edge function to accept invitation
        const { data, error } = await supabase.functions.invoke('accept-invitation', {
          body: { invitation_id: invitationData.invitation_id }
        });
        
        if (error) {
          console.error('Error accepting invitation:', error);
          notifyErrorLocal(
            tPages('notifications.error'),
            tPages('notifications.failed_accept', { error: (error as any).message || '' })
          );
          return;
        }
        
        const result = data as { success: boolean; error?: string; message?: string; company_name?: string };
        
        if (!result.success) {
          notifyErrorLocal(
            tPages('notifications.error'),
            result.message || tPages('notifications.failed_accept')
          );
          return;
        }
        
        // Show immediate success toast
        const companyName = result.company_name || invitationData?.company_name || tPages('notifications.the_company');
        toast.success('Invitation Accepted', {
          description: `You've successfully joined ${companyName}!`
        });
        
        // Refresh data after invitation acceptance
        await fullRefresh();
        console.log('✅ Auth data refreshed after invitation acceptance');
        
      } else {
        // Call edge function to decline invitation (silent - no notification)
        const { data, error } = await supabase.functions.invoke('decline-invitation', {
          body: { invitation_id: invitationData.invitation_id }
        });
        
        if (error) {
          console.error('Error declining invitation:', error);
          notifyErrorLocal(
            tPages('notifications.error'),
            tPages('notifications.failed_decline')
          );
          return;
        }
        
        // Decline is silent - no success notification shown
      }
      
      // Force refresh notifications to update UI immediately
      await loadNotifications();
      
    } catch (error) {
      console.error('Error processing invitation:', error);
      notifyErrorLocal(
        tPages('notifications.error'),
        tPages('notifications.failed_process_invitation')
      );
    } finally {
      setProcessingInvitation(null);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'company_invitation': return <UserPlus className="w-3.5 h-3.5 text-blue-600" />;
      case 'recruiter_application_invitation': return <Briefcase className="w-3.5 h-3.5 text-green-600" />;
      case 'candidate_ai_interview_completed': return <Calendar className="w-3.5 h-3.5 text-purple-600" />;
      case 'ai_interview_completed': return <Calendar className="w-3.5 h-3.5 text-purple-600" />;
      case 'application': return <Users className="w-3.5 h-3.5 text-green-600" />;
      case 'interview': return <Calendar className="w-3.5 h-3.5 text-purple-600" />;
      case 'grading': return <Briefcase className="w-3.5 h-3.5 text-orange-600" />;
      case 'chat': return <MessageSquare className="w-3.5 h-3.5 text-blue-600" />;
      default: return <Bell className="w-3.5 h-3.5" />;
    }
  };
  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d`;
    }
  };

  return (
    <>
      <div className="flex items-center justify-between p-2.5 sm:p-3 border-b">
        <HeadingSmall>{tPages('notifications.title')}</HeadingSmall>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/notifications')}
          className="text-xs h-7 min-w-[44px] px-2"
        >
          {tPages('notifications.view_all')}
        </Button>
      </div>
      
      <ScrollArea className="h-80">
        {notifications.length === 0 ? (
          <div className="p-3 sm:p-4 text-center text-muted-foreground">
            <Bell className="w-5 h-5 mx-auto mb-2 opacity-50" />
            <p className="text-xs">{tPages('notifications.no_notifications')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {notifications.slice(0, 10).map((notification) => (
              <div
                key={notification.id}
                className={`p-3 sm:p-4 hover:bg-accent/50 transition-colors border-l-2 ${
                  notification.read 
                    ? 'border-l-transparent opacity-60' 
                    : 'border-l-primary'
                }`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-muted rounded-full flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-1">
                    <div className="flex items-center gap-1.5">
                      <HeadingSmall className="truncate">
                        {notification.title}
                      </HeadingSmall>
                      {!notification.read && (
                        <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                      )}
                    </div>
                    
                    <div 
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (notification.message.length > 100) {
                          setExpandedId(expandedId === notification.id ? null : notification.id);
                        }
                      }}
                    >
                      <BodySmall className={expandedId === notification.id ? "whitespace-pre-wrap" : "line-clamp-2"}>
                        {notification.message}
                      </BodySmall>
                      {notification.message.length > 100 && (
                        <Caption className="text-primary/60 hover:text-primary font-medium mt-1 block">
                          {expandedId === notification.id ? tUI('show_less') : tUI('show_more')}
                        </Caption>
                      )}
                    </div>
                    
                    {notification.is_invitation && (
                      <div className="flex gap-2 pt-2 flex-wrap sm:flex-nowrap">
                        <Button 
                          size="sm"
                          onClick={() => handleInvitationResponse(notification.id, true)}
                          disabled={processingInvitation === notification.id}
                          className="h-9 px-3 text-xs min-w-[44px] flex-1 sm:flex-initial"
                        >
                          <Check className="w-3 h-3 mr-1" />
                          {processingInvitation === notification.id ? tUI('processing') : tPages('notifications.accept')}
                        </Button>
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => handleInvitationResponse(notification.id, false)}
                          disabled={processingInvitation === notification.id}
                          className="h-9 px-3 text-xs min-w-[44px] flex-1 sm:flex-initial bg-background border-border hover:bg-accent"
                        >
                          <X className="w-3 h-3 mr-1" />
                          {processingInvitation === notification.id ? tUI('processing') : tPages('notifications.decline')}
                        </Button>
                      </div>
                    )}

                    {/* Recruiter Application Invitation Actions */}
                    {notification.type === 'recruiter_application_invitation' && (
                      <div className="flex gap-2 pt-2 flex-wrap sm:flex-nowrap">
                        <Button 
                          size="sm"
                          onClick={() => {
                            openJobFromNotification(notification.data);
                            markAsRead(notification.id);
                          }}
                          disabled={jobLoading}
                          className="h-9 px-3 text-xs min-w-[44px] flex-1 sm:flex-initial"
                        >
                          <Briefcase className="w-3 h-3 mr-1" />
                          {tUI('view_and_apply')}
                        </Button>
                      </div>
                    )}

                    
                    <div className="flex items-center justify-between">
                      <Caption>{formatTimeAgo(notification.created_at)}</Caption>
                      {!notification.read && !notification.is_invitation && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(notification.id)}
                          className="h-8 px-2 text-xs hover:text-primary min-w-[44px]"
                        >
                          {tUI('mark_read')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Enhanced Job Detail Dialog for Recruiter Invitations */}
      {job && (
        <JobDetailDialog
          job={job}
          open={dialogOpen}
          onOpenChange={closeDialog}
          onApplicationSubmitted={() => {
            closeDialog();
            loadNotifications(); // Refresh notifications after application
          }}
        />
      )}
    </>
  );
}